/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars, testing-library/render-result-naming-convention */
import React, { useState, useMemo, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Box,
    IconButton,
    Popover,
    Typography
} from "@mui/material";
import styles from "./alertTables.module.css";
import CustomTooltip from "utils/CustomTooltip";
import Tooltip from "@mui/material/Tooltip";
import { StyledTableCell, StyledTableRow, StyledTableSubHeaderCell } from "./materialUITableStyling";
import { trimQuotes } from "utils/trimQuotes";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandRight from "../../../../assets/images/expandRight.svg";
import AlertPagination from "./AlertPagination";
import { flattenMaterialGroups, buildMaterialDisplayRows } from "./materialGrouping";
import PODetailsMAI from "Samarth Module/components/popup/PODetailsMAI";
import { getPODetailsMAIPage } from "Samarth Module/api/api";
import { getISOWeek } from "date-fns";
import SupplierPopup from "Samarth Module/components/popup/SupplierPopup";
import {columnDesc} from "../../../../utils/columndesc";

const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];
const STOCK_COLOR_ALERT = "RMPM Alert";
const EMPTY_CELL_VALUE = "-";
const STOCK_AGEING_COLUMN =
"Stock Ageing of Unrestricted Stock (Qty)";
 
const STOCK_AGEING_MORE_DETAILS =
"More Details";

const getDefaultHeatMapColor = (value) => {
    if (value === null) return "#f5f5f5 !important";
    if (value === 0) return "#feb0af !important";
    return "inherit";
};

const getMaiTotalHeatMapColor = (isAfterThresholdWeek, value) => {
    if (isAfterThresholdWeek) return "#D3D3D3 !important";
    return getDefaultHeatMapColor(value);
};

const getStableRowKey = (alert) => {
    const parts = [
        alert?.["Alert ID"],
        alert?.["Material Id"],
        alert?.["Site code"],
        alert?.["Vendor name"],
        alert?.["Material description"]
    ].filter(Boolean);

    return parts.length ? parts.join("-") : "row";
};

const MAITable = ({
    data,
    columnList,
    paginationObj,
    onPageChange,
    loading,
    perWeekMai,
    perWeekMaiPhysical,
    totalStockToNorm,
    subtotal,
    onSortChange,
    sortConfig,
    groupByMaterialId = false,
}) => {
    const normalizedData = useMemo(
        () => groupByMaterialId ? flattenMaterialGroups(data) : data,
        [data, groupByMaterialId],
    );
    const [localItems, setLocalItems] = useState(normalizedData);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [isProcessing, setIsProcessing] = useState(false);
    const [expandedColumns, setExpandedColumns] = useState({});
    const [expandedCellValues, setExpandedCellValues] = useState({});
    const [showPODetailsPopup, setShowPODetailsPopup] = useState(false);
    const [poDetailsData, setPoDetailsData] = useState({ tableData: [], rowData:{} });
    const [showSupplierDetailsPopup, setShowSupplierDetailsPopup] = useState(false);
    const [supplierDetailsData, setSupplierDetailsData] = useState([]);
    const [columnInfoAnchor, setColumnInfoAnchor] = useState(null);
    const [columnInfoText, setColumnInfoText] = useState("");
    const [showStockAgeingDetailsPopup, setShowStockAgeingDetailsPopup] = useState(false);

    const [stockAgeingDetailsData, setStockAgeingDetailsData] = useState({ rowData: {} });

    const current_week = getISOWeek(new Date());

    // Apply heatmap/background coloring only for selected metric columns.
    const colorEligibleColumns = useMemo(
        () => new Set(["MAI % (Total)", "MAI % (Physical)", "Stock to Norm %"]),
        []
    );

    const shouldApplyCellColor = useCallback(
        (label) => colorEligibleColumns.has(label),
        [colorEligibleColumns]
    );

    // Handler for PO Details click
    const handlePODetailsClick = async (row) => {
        const payload = {
            email: localStorage.getItem("UserInfo") ? JSON.parse(localStorage.getItem("UserInfo")).email : "",
            type: 'MAI',
            "Material Id": row["Material Id"],
            "Site code": row["Site code"],
        };
        const getData = await getPODetailsMAIPage(payload);
        setPoDetailsData({ tableData: getData?.poDetails || [], rowData: row });
        setShowPODetailsPopup(true);
    };

    const handleSupplierDetailsClick = (alert) => {
        if(alert['Physical Stock (Qty)']?.['Supplier Stock'] === "-"){
            return ;
        }
        const supplierInfo=alert['Supplier Details']
        const result = supplierInfo.split(',').map(item => {
        return item.split(':').map(x => x.trim());
    })   
    const data=result.map((item =>{
        return {"Material Id":alert['Material Id'],"Material Name":alert['Material description'],"Supplier Code":item[0],"Supplier Name":item[1],"Supplier Stock":item[2]}
    }))
        setSupplierDetailsData(data);
        setShowSupplierDetailsPopup(true);
    };


    const handlePODetailsPopup = () => {
        setShowPODetailsPopup(prev => !prev);
        setPoDetailsData({ tableData: [], rowData: {} });
    };

    const handleSupplierDetailsPopup = () => {
        setShowSupplierDetailsPopup(prev => !prev);
        setSupplierDetailsData([]);
    }

    const get_tooltip=(value)=>{
        if(value==='Extremely High'){
            return "If Stock Cover (Days) > 1824  = Extremely High"
        }
        else if(value==='Very High'){
            return "If 364 < Stock Cover (Days) < 1825 = Very High"
        }
        else if(value==='High'){
            return "If 89 < Stock Cover (Days) < 365 =  High"
        }
        else if(value==="No Demand But Stock Available"){
            return "If Avg Daily Demand = 0"
        }
        return ""
    }

    const displayColumn = "MAI % (Total)";

    const materialIdVisible = useMemo(() =>
        columnList.some(col => col.label === "Material Id" && col.visibility === true),
        [columnList]
    );
    const materialDescVisible = useMemo(() =>
        columnList.some(col => col.label === "Material description" && col.visibility === true),
        [columnList]
    );

     const siteCode = useMemo(() =>
        columnList.some(col => col.label === "Site code" && col.visibility === true),
        [columnList]
    );
    const siteCluster = useMemo(() =>
        columnList.some(col => col.label === "Site cluster" && col.visibility === true),
        [columnList]
    );
    
    const showableColumns=["Site code","Site cluster","MAI % (Total)","MAI % (Physical)"];
    const visibleSubLabels = useMemo(
        () => columnList.filter((col) => col.visibility === true && col.label.startsWith('Sub_')).map((col) => col.label),
        [columnList]
    );
    const renderableSubLabels = useMemo(() => {
        const supplierStockSubLabel = "Sub_Physical Stock (Qty)_Supplier Stock";
        return visibleSubLabels.filter((label) => materialIdVisible || label !== supplierStockSubLabel);
    }, [visibleSubLabels, materialIdVisible]);
    const hideLabels=useMemo(()=> columnList.filter(col=>!showableColumns.includes(col.label)).map(col=>col.label),[columnList]);
    const shouldShowStockColumns = materialIdVisible || materialDescVisible;
    const shouldShowSomeColumns= siteCode || (siteCode && siteCluster);
    

    const columnsToExclude = useMemo(() =>
        shouldShowStockColumns && shouldShowSomeColumns? [] :
        shouldShowStockColumns && !shouldShowSomeColumns?[
            "Lead Time(Min-Max)",
            "Inter-unit transfer view",
            "Stock VS Norm"
        ] :
        hideLabels,
        [shouldShowStockColumns, shouldShowSomeColumns]
    );

    // Memoized selected columns - ALL visible columns in columnList order
    const selectedColumns = useMemo(() =>
        columnList.filter(a =>
            a.visibility === true &&
            !columnsToExclude.includes(a.label) && !visibleSubLabels.includes(a.label)
        ),
        [columnList, columnsToExclude, visibleSubLabels]
    );

    const physicalStock= useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const allPhysicalStock = [
          "Physical Stock (Qty)"
        ];

        const visibleLabels = selectedColumns.map(col => col.label);
        return allPhysicalStock.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

    const physicalStockValuesCols = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const allPhysicalStockValuesCols = [
          "Physical Stock (INR)"
        ];

        const visibleLabels = selectedColumns.map(col => col.label);
        return allPhysicalStockValuesCols.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

      const remainingColumns = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const remaining = [
            "Past Due PO"
        ];
        const visibleLabels = selectedColumns.map(col => col.label);
        return remaining.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

    const fourWeekColumns = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const allfourWeekColumns = [
            "Future Open PO"
        ];
        const visibleLabels = selectedColumns.map(col => col.label);
        return allfourWeekColumns.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

     const allWeekColumns = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const allPossibleWeekColumns = [
            "Demand"
        ];
        const visibleLabels = selectedColumns.map(col => col.label);
        return allPossibleWeekColumns.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

      const twoWeekColumns = useMemo(() => {
        if (!shouldShowStockColumns) return ["MAI % (Physical)"];

        const alltwoWeekColumns = [
            "Stock (Physical)",
            "MAI % (Physical)"
        ];
        const visibleLabels = selectedColumns.map(col => col.label);
        return alltwoWeekColumns.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);


     const maiphysicalColumns = useMemo(() => {
        if (!shouldShowStockColumns) return ["MAI % (Physical)"];

        const allWeekColumns = [
            "MAI % (Physical)"
        ];
        const visibleLabels = selectedColumns.map(col => col.label);
        return allWeekColumns.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

        const consumptionCol = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const consCol= [
          "Consumption"
        ];

        const visibleLabels = selectedColumns.map(col => col.label);
        return consCol.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);


    const interUnitCol = useMemo(() => {
        if (!shouldShowStockColumns) return [];

        const interUnit= [
          "Inter-unit transfer view"
        ];

        const visibleLabels = selectedColumns.map(col => col.label);
        return interUnit.filter(col => visibleLabels.includes(col));
    }, [shouldShowStockColumns, selectedColumns]);

    // Effect for data synchronization
    useEffect(() => {
        setLocalItems(normalizedData);
        setPage(0);
        setIsProcessing(loading);
    }, [normalizedData, paginationObj, onPageChange, loading]);

    // Get week keys from data
    const getWeekKeys = useCallback((column, limit = Infinity) => {
        const preserveOriginalOrderColumns = new Set([
            "Physical Stock (INR)",
            "Physical Stock (Qty)",
            "Inter-unit transfer view",
            "Stock VS Norm",
            "Stock Ageing of Unrestricted Stock (Qty)"
        ]);

    const allWeeks = [...new Set(
        localItems.flatMap((item) => {
            if (column === "Stock VS Norm") {
                return renderableSubLabels
                    .filter((label) =>
                        label.startsWith("Sub_Stock VS Norm_")
                    )
                    .map((label) =>
                        label.replace("Sub_Stock VS Norm_", "")
                    );
            }
            if (
                column === "Physical Stock (INR)" ||
                column === "Physical Stock (Qty)" ||
                column === "Inter-unit transfer view"
            ) {
                return Object.keys(item[column] || {}).filter((key) =>
                    renderableSubLabels.includes(`Sub_${column}_${key}`)
                );
            }

            if (column === STOCK_AGEING_COLUMN) {
                return Object.keys(item[column] || {}).filter((key) =>
                    renderableSubLabels.includes(`Sub_${column}_${key}`)
                );
            }

            if (
                column === "Future Open PO" &&
                item[column]
            ) {
                return Object.keys(item[column] || {}).filter(
                    (key) =>
                        (key.startsWith("Wk") ||
                            key.startsWith("Total")) &&
                        renderableSubLabels.includes(
                            `Sub_${column}_${key}`
                        )
                );
            }
            if (typeof column === "string" && item[column]) {
                return Object.keys(item[column]).filter(
                    (key) =>
                        (key.startsWith("Wk") || key.startsWith("N")) &&
                        renderableSubLabels.includes(`Sub_${column}_${key}`)
                );
            }

            return [];
        })
    )];

        if (column === STOCK_AGEING_COLUMN) {
            const orderedAgeingKeys = renderableSubLabels
                .filter((label) =>
                    label.startsWith(`Sub_${STOCK_AGEING_COLUMN}_`)
                )
                .map((label) =>
                    label.replace(`Sub_${STOCK_AGEING_COLUMN}_`, "")
                );

            const availableKeySet = new Set(allWeeks);

            const visibleAgeingKeys = [...new Set(orderedAgeingKeys)]
                .filter((key) => availableKeySet.has(key));

            return [
                ...visibleAgeingKeys,
                STOCK_AGEING_MORE_DETAILS,
            ].slice(0, limit);
        }

        if (preserveOriginalOrderColumns.has(column)) {
            return allWeeks.slice(0, limit);
        }

        const orderedSubWeeks = renderableSubLabels
        .filter(
            (label) =>
                typeof column === "string" &&
                label.startsWith(`Sub_${column}_`)
        )
        .map((label) =>
            label.replace(`Sub_${column}_`, "")
        );

    if (orderedSubWeeks.length > 0) {
        const availableWeekSet = new Set(allWeeks);

        const orderedWeeks = [...new Set(orderedSubWeeks)]
            .filter((key) => availableWeekSet.has(key));

        return orderedWeeks.slice(0, limit);
    }

    return allWeeks.slice(0, limit);
}, [localItems, renderableSubLabels]);

    
    const maiWeekKeys = useMemo(() => getWeekKeys(displayColumn, 19), [getWeekKeys, displayColumn]);
    const otherWeekKeys = useMemo(() => fourWeekColumns[0] ? getWeekKeys(fourWeekColumns[0], 5) : [], [getWeekKeys, fourWeekColumns]);
    const twoWeekKeys = useMemo(() => twoWeekColumns[0] ? getWeekKeys(twoWeekColumns[0], 4) : [], [getWeekKeys, twoWeekColumns]);
    const maiPhysicalKeys=useMemo(() => maiphysicalColumns[0] ? getWeekKeys(maiphysicalColumns[0], 19) : [], [getWeekKeys, maiphysicalColumns]);
    const threeTableKeys = useMemo(() => remainingColumns[0] ? getWeekKeys(remainingColumns[0]) : [], [getWeekKeys, remainingColumns]);
    const allWeekKeys = useMemo(() => allWeekColumns[0] ? getWeekKeys(allWeekColumns[0], 20) : [], [getWeekKeys, allWeekColumns]);
    const physicalStockKeys = useMemo(() => physicalStockValuesCols[0] ? getWeekKeys(physicalStockValuesCols[0]) : [], [getWeekKeys, physicalStockValuesCols]);
    const physicalStockQ = useMemo(() => physicalStock[0] ? getWeekKeys(physicalStock[0]) : [], [getWeekKeys, physicalStock]);
    const stockVsNormKeys = useMemo(() => getWeekKeys("Stock VS Norm"), [getWeekKeys]);
    const consumptionKeys = useMemo(() => consumptionCol[0] ? getWeekKeys(consumptionCol[0]) : [], [getWeekKeys, consumptionCol]);
    const interUnitKeys = useMemo(() => interUnitCol[0] ? getWeekKeys(interUnitCol[0]) : [], [getWeekKeys, interUnitCol]);

    // Create ordered column configuration based on columnList order
    const orderedColumnConfig = useMemo(() => {
        const config = [];
        const stockVsNormSubLabels = visibleSubLabels
            .filter((label) => label.startsWith("Sub_Stock VS Norm_"))
            .map((label) => label.replace("Sub_Stock VS Norm_", ""));
        const stockVsNormSubLabelSet = new Set(stockVsNormSubLabels);
        const hasStockVsNormParent = selectedColumns.some((col) => col.label === "Stock VS Norm");

        const weekBasedColumns = {
            'Physical Stock (Qty)': { keys: physicalStockQ, type: 'week-based', category: 'physicalStock' },
            'Physical Stock (INR)': { keys: physicalStockKeys, type: 'week-based', category: 'physicalStockValues' },
            'Stock VS Norm': { keys: stockVsNormKeys, type: 'week-based', category: 'stockVsNorm' },
            'Past Due PO': { keys: threeTableKeys, type: 'week-based', category: 'pastDuePO' },
            'Future Open PO': { keys: otherWeekKeys, type: 'week-based', category: 'fourWeek' },
            'Demand': { keys: allWeekKeys, type: 'week-based', category: 'allWeek' },
            'Stock (Physical)': { keys: twoWeekKeys, type: 'week-based', category: 'twoWeek' },
            'MAI % (Physical)': { keys: maiPhysicalKeys, type: 'week-based', category: 'maiPhysical' },
            'Consumption': { keys: consumptionKeys, type: 'week-based', category: 'consumption' },
            'Inter-unit transfer view': { keys: interUnitKeys, type: 'week-based', category: 'interUnit' },
            'MAI % (Total)': { keys: maiWeekKeys, type: 'week-based', category: 'maiTotal' }
        };

        const fixedColumnLabels = ['Material Id', 'Material description', 'CBU', 'CBU description', 'Site code', 'Site cluster'];

        selectedColumns.forEach((col, index) => {
            if (hasStockVsNormParent && stockVsNormSubLabelSet.has(col.label)) {
                return;
            }

            const isFixed = fixedColumnLabels.includes(col.label);
            
            if (weekBasedColumns[col.label]) {
                const weekConfig = weekBasedColumns[col.label];
                config.push({
                    ...col,
                    isFixed,
                    type: 'week-based',
                    weekKeys: weekConfig.keys,
                    category: weekConfig.category,
                    originalIndex: index
                });
            } else if (col.label === 'PO Details') {
                config.push({
                    ...col,
                    isFixed: false,
                    type: 'po-details',
                    originalIndex: index
                });
            } else {
                // Auto-detect if column contains object data (week-based)
                const sampleValue = localItems.length > 0 ? localItems[0][col.label] : null;
                if (sampleValue && typeof sampleValue === 'object' && !Array.isArray(sampleValue)) {
                    // This is a week-based column that wasn't explicitly mapped
                    const detectedKeys = getWeekKeys(col.label);
                    config.push({
                        ...col,
                        isFixed,
                        type: 'week-based',
                        weekKeys: detectedKeys,
                        category: 'autoDetected',
                        originalIndex: index
                    });
                } else {
                    config.push({
                        ...col,
                        isFixed,
                        type: 'simple',
                        originalIndex: index
                    });
                }
            }
        });

        return config;
    }, [selectedColumns, visibleSubLabels, physicalStockQ, physicalStockKeys, stockVsNormKeys, threeTableKeys, otherWeekKeys, allWeekKeys, twoWeekKeys, consumptionKeys, interUnitKeys,maiPhysicalKeys, maiWeekKeys, localItems, getWeekKeys]);
    // Keep one source of truth for UI order from columnList
    const columnsInDisplayOrder = useMemo(() =>
        [...orderedColumnConfig].sort((a, b) => a.originalIndex - b.originalIndex),
        [orderedColumnConfig]
    );

    const fixedColumns = useMemo(() =>
        columnsInDisplayOrder.filter(col => col.isFixed),
        [columnsInDisplayOrder]
    );

    // Calculate left positions for fixed columns
    const fixedColumnPositions = useMemo(() => {
        const positions = [];
        let left = 0;

        fixedColumns.forEach((col, _index) => {
            positions.push(left);
            left += col.width || 100;
        });

        return positions;
    }, [fixedColumns]);

    const fixedColumnLeftByLabel = useMemo(() => {
        const leftByLabel = {};
        fixedColumns.forEach((col, index) => {
            leftByLabel[col.label] = fixedColumnPositions[index];
        });
        return leftByLabel;
    }, [fixedColumns, fixedColumnPositions]);

    const removeExpandedValuesForColumn = (prevCellValues, columnLabel) => {
        const updated = {};
        Object.keys(prevCellValues).forEach((key) => {
            if (!key.endsWith(`__${columnLabel}`)) {
                updated[key] = prevCellValues[key];
            }
        });
        return updated;
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation(); // Prevent triggering sort
        setExpandedColumns(prev => {
            const isExpanding = !prev[columnLabel];

            if (!isExpanding && supportsCommaExpand(columnLabel)) {
                setExpandedCellValues((prevCellValues) =>
                    removeExpandedValuesForColumn(prevCellValues, columnLabel)
                );
            }

            return {
                ...prev,
                [columnLabel]: isExpanding,
            };
        });
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return label === "Material description" || label==='CBU' || label==='CBU description';
    };

    const supportsCommaExpand = (label) => label === "CBU" || label === "CBU description";

    const getExpandedWidthForColumn = (label, defaultWidth) =>
        label === "CBU description" ? '500px' : defaultWidth;

    const getCommaSeparatedValues = (value) =>
        String(trimQuotes(value) || "")
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean);

    const getRenderableCellValue = (inputValue) => {
        if (inputValue === null || inputValue === undefined) {
            return inputValue;
        }

        if (typeof inputValue !== "object") {
            return inputValue;
        }

        const preferredKeys = ["N-7", "N-30", "N-180"];
        for (const key of preferredKeys) {
            if (Object.prototype.hasOwnProperty.call(inputValue, key) && typeof inputValue[key] !== "object") {
                return inputValue[key];
            }
        }

        const firstPrimitive = Object.values(inputValue).find((val) => val === null || typeof val !== "object");
        return firstPrimitive !== undefined ? firstPrimitive : "-";
    };

    const renderCommaSeparatedValueWithEllipsis = (value) => {
        if (value === undefined || value === null || value === "") {
            return "-";
        }

        const normalizedValue = trimQuotes(value);
        const textValue = String(normalizedValue || "").trim();
        const materialDescriptionWidth = getExpandedWidthForColumn("Material description", "250px");
        const shouldTruncate = textValue.includes(",") || textValue.length > 30;

        if (!shouldTruncate) {
            return textValue || "-";
        }

        return (
            <CustomTooltip title={textValue}>
                <span
                    style={{
                        display: "inline-block",
                        maxWidth: materialDescriptionWidth,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "bottom",
                    }}
                >
                    {textValue}
                </span>
            </CustomTooltip>
        );
    };

    const getCellExpandKey = (rowIndex, label) => `${rowIndex}__${label}`;

    // API-based sorting handler
    const handleSort = useCallback((key, week) => {
        if (onSortChange) {
            const mapWeekToSortKeyColumns = new Set([
                "Physical Stock (INR)",
                "Physical Stock (Qty)",
                "Inter-unit transfer view",
                "Stock VS Norm",
                "Past Due PO"
            ]);

            const useWeekAsSortKey = Boolean(week) && mapWeekToSortKeyColumns.has(key);
            const effectiveKey = useWeekAsSortKey ? week : key;
            const effectiveWeek = useWeekAsSortKey ? null : (week || null);

            const newDirection = sortConfig?.key === effectiveKey && sortConfig?.direction === "asc"
                ? "desc"
                : "asc";

            onSortChange({ key: effectiveKey, direction: newDirection, week: effectiveWeek });
        }
    }, [sortConfig, onSortChange]);

    const getSubtotalRawValue = useCallback((col, week) => {
        if (col.category === 'maiPhysical') {
            return perWeekMaiPhysical[week.replace(/\s+/g, "") + '_Covered_NotCovered_NA_Physical'];
        }

        if (col.category === 'maiTotal') {
            return perWeekMai[week.replace(/\s+/g, "") + '_Covered_NotCovered_NA'];
        }

        if (col.category === 'stockVsNorm') {
            if (week === 'Stock to Norm %') {
                return totalStockToNorm;
            }
            return subtotal[col.label]?.[week] ?? subtotal[week];
        }

        return subtotal[col.label]?.[week];
    }, [subtotal, perWeekMai, perWeekMaiPhysical, totalStockToNorm]);

    // Use data directly (already sorted by API)
    const sortedData = normalizedData;

    // Handlers for pagination
    const handleChangePage = useCallback((event, newPage, pageSize = rowsPerPage) => {
        if (!paginationObj && !onPageChange) {
            setPage(newPage);
        } else {
            const actualPage = newPage + 1;
            onPageChange?.(actualPage, pageSize);
        }
    }, [paginationObj, onPageChange, rowsPerPage]);

    const handleChangeRowsPerPage = useCallback((newRowsPerPage) => {
        setIsProcessing(true); // Set processing state
        setRowsPerPage(newRowsPerPage);

        if (!paginationObj && !onPageChange) {
            setPage(0);
        } else {
            onPageChange?.(1, newRowsPerPage);
        }
    }, [paginationObj, onPageChange]);

    // Calculate total rendered columns for colspan
    const totalRenderedColumns = useMemo(() => {
        let total = 0;
        columnsInDisplayOrder.forEach(col => {
            if (col.type === 'week-based' && col.weekKeys) {
                total += col.weekKeys.length;
            } else {
                total += 1;
            }
        });
        return total;
    }, [columnsInDisplayOrder]);

    const handleStockAgeingDetailsClick = async (row) => {
        // Call your Stock Ageing details API here.
        const shelfData = row["Total Shelf Life Data"]?.map((item) => {
            const shelfItem = {
                "Site code": item["Site code"],
                "Site cluster": item["Site Cluster"],
                "Material ID": item["Material ID"],
                "Material Description": item["Material Description"],
                "UOM": item["UOM"],
                "Batch Number":item["batch"],
                "Qty":item["Unrestricted_Stock_Qty"],
                "MFG Date":item["Mfg_Date"],
                "Expiry Date":item["Expiry_Date"],
                "Shelf Life":item["Shelf_Life"],
                "Days left from expiry":item["Days_left_from_expiry"],
            };
            return shelfItem;
        }) || [];
        
        setStockAgeingDetailsData({rowData: shelfData});
        setShowStockAgeingDetailsPopup(true);
    };

    const handleStockAgeingDetailsPopup = () => {
        setShowStockAgeingDetailsPopup(false);
        setStockAgeingDetailsData({rowData: {}});
    };

    // Render functions
    const renderMainHeader = useCallback(() => {
        // Helper to get background color for parent-child sets
        const getParentChildColor = (_setIndex) => {
            // Odd sets get light blue, even sets get dark blue
            return '#354ACD';
        };

        let colorIndex = 0;

        return (
            <TableRow>
                {columnsInDisplayOrder.map((col) => {
                    
                    if (col.isFixed) {
                        return (
                            <StyledTableCell
                                key={`fixed-col-${col.label}`}
                                onClick={() => handleSort(col.label)}
                                sx={{
                                    position: "sticky",
                                    left: fixedColumnLeftByLabel[col.label],
                                    zIndex: 15,
                                    backgroundColor: "white",
                                    minWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '300px') : (col.width || "100px"),
                                    maxWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '300px') : undefined,
                                    width: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '300px') : (col.width || "100px"),
                                    cursor: "pointer",
                                }}
                            >
                                <div className={styles.sortIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                    <span style={{ flex: 1 }}>{col.label}</span>
                                    <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                        {Boolean(getColumnDescription(col.label)) && (
                                            <IconButton
                                                size="small"
                                                aria-label={`${col.label} info`}
                                                onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                                sx={{ padding: '1px', color: '#ffffff' }}
                                            >
                                                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                            </IconButton>
                                        )}
                                        {sortConfig?.key === col.label && (
                                            sortConfig?.direction === 'desc' ? <ArrowDownwardIcon fontSize="12px" /> : <ArrowUpwardIcon fontSize="12px" />
                                        )}
                                        {isTruncatableColumn(col.label) && (
                                            <button
                                                type="button"
                                                onClick={(e) => toggleColumnExpansion(col.label, e)}
                                                title="Expand/Collapse column"
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    cursor: "pointer",
                                                    border: "none",
                                                    background: "transparent",
                                                    padding: 0
                                                }}
                                            >
                                                <img src={ExpandRight} alt="expandRight" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </StyledTableCell>
                        );
                    }

                    if (col.type === 'po-details') {
                        return (
                            <StyledTableCell
                                key="po-details-header"
                                sx={{
                                    minWidth: "100px",
                                    width: "100px",
                                }}
                            >
                                {col.label}
                                {Boolean(getColumnDescription(col.label)) && (
                                        <IconButton
                                            size="small"
                                            aria-label={`${col.label} info`}
                                            onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                            sx={{ padding: '1px', color: '#ffffff' }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    )
                                }
                            </StyledTableCell>
                        );
                    } else if (col.type === 'week-based' && col.weekKeys && col.weekKeys.length > 0) {
                        colorIndex++;
                        return (
                            <StyledTableCell
                                key={`scroll-header-${col.label}`}
                                colSpan={col.weekKeys.length}
                                sx={{
                                    backgroundColor: getParentChildColor(colorIndex) + ' !important',
                                    color: getParentChildColor(colorIndex) === '#C2E0FF' ? '#000000 !important' : 'rgb(255, 255, 255) !important',
                                    border: getParentChildColor(colorIndex) === '#C2E0FF' ? '1px solid #000000 !important' : undefined
                                }}
                            >
                                {col.label}
                                {Boolean(getColumnDescription(col.label)) && (
                                    <IconButton
                                        size="small"
                                        aria-label={`${col.label} info`}
                                        onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                        sx={{ padding: '1px', color: '#ffffff' }}
                                    >
                                        <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                    </IconButton>
                                )}
                            </StyledTableCell>
                        );
                    } else {
                        return (
                            <StyledTableCell
                                key={`single-col-${col.label}`}
                                onClick={() => handleSort(col.label)}
                                sx={{
                                    cursor: "pointer",
                                }}
                            >
                                <div className={styles.sortIcon}>
                                    {col.label}
                                    {Boolean(getColumnDescription(col.label)) && (
                                        <IconButton
                                            size="small"
                                            aria-label={`${col.label} info`}
                                            onClick={(event) => handleOpenColumnInfo(event, col.label)}
                                            sx={{ padding: '1px', color: '#ffffff' }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                                        </IconButton>
                                    )}
                                    {sortConfig?.key === col.label && (
                                        sortConfig?.direction === 'desc' ? <ArrowDownwardIcon fontSize="12px" /> : <ArrowUpwardIcon fontSize="12px" />
                                    )}
                                </div>
                            </StyledTableCell>
                        );
                    }
                })}
            </TableRow>
        );
    }, [columnsInDisplayOrder, fixedColumnLeftByLabel, expandedColumns, sortConfig, handleSort]);

    const renderWeekHeaders = useCallback(() => {
        // Helper to get background color for parent-child sets
        const getParentChildColor = (_setIndex) => {
            return '#354ACD';
        };
        const getTextColor = (_setIndex) => {
            return '#FFFFFF';
        };

        let colorIndex = 0;
        return (
            <>
                <TableRow>
                    {columnsInDisplayOrder.map((col) => {
                        if (col.isFixed) {
                            return (
                                <StyledTableSubHeaderCell
                                    key={`fixed-placeholder-${col.label}`}
                                    sx={{
                                        position: "sticky",
                                        left: fixedColumnLeftByLabel[col.label],
                                        zIndex: 14,
                                        backgroundColor: "#f5f5f5",
                                        minWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                        maxWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : undefined,
                                        width: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                    }}
                                />
                            );
                        }

                        if (col.type === 'po-details') {
                            return (
                                <StyledTableSubHeaderCell
                                    key="po-details-placeholder"
                                    sx={{
                                        backgroundColor: "#f5f5f5",
                                        minWidth: "100px",
                                        width: "100px",
                                    }}
                                />
                            );
                        } else if (col.type === 'week-based' && col.weekKeys && col.weekKeys.length > 0) {
                            colorIndex++;
                            return col.weekKeys.map((week, weekIndex) => {
                                const isStockAgeingMoreDetails =
                                    col.label === STOCK_AGEING_COLUMN &&
                                    week === STOCK_AGEING_MORE_DETAILS;
                                return (
                                    <StyledTableSubHeaderCell
                                        key={`${col.label}-week-${String(week)}`}
                                        onClick={
                                            isStockAgeingMoreDetails
                                                ? undefined
                                                : () => handleSort(col.label, week)
                                        }
                                        sx={{
                                            cursor: isStockAgeingMoreDetails
                                                ? "default"
                                                : "pointer",
                                            borderLeft: weekIndex === 0
                                                ? "1px solid #00000063 !important"
                                                : "inherit",
                                            backgroundColor:
                                                getParentChildColor(colorIndex) + " !important",
                                            color: getTextColor(colorIndex) + " !important",
                                        }}
                                    >
                                        <div className={styles.sortIcon}>
                                            {week}

                                            {!isStockAgeingMoreDetails &&
                                                sortConfig?.key === col.label &&
                                                sortConfig?.week === week &&
                                                (
                                                    sortConfig?.direction === "desc"
                                                        ? <ArrowDownwardIcon fontSize="12px" />
                                                        : <ArrowUpwardIcon fontSize="12px" />
                                                )}
                                        </div>
                                    </StyledTableSubHeaderCell>
                                )
                            });
                        } else {
                            return (
                                <StyledTableSubHeaderCell
                                        key={`${col.label}-placeholder`}
                                    />
                            );
                        }
                    })}
                </TableRow>
                <TableRow>
                    {columnsInDisplayOrder.map((col) => {
                        if (col.isFixed) {
                            return (
                                <StyledTableSubHeaderCell
                                    key={`fixed-placeholder-2-${col.label}`}
                                    sx={{
                                        position: "sticky",
                                        left: fixedColumnLeftByLabel[col.label],
                                        zIndex: 14,
                                        backgroundColor: "#f5f5f5",
                                        minWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                        maxWidth: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : undefined,
                                        width: expandedColumns[col.label] ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                    }}
                                />
                            );
                        }

                        if (col.type === 'po-details') {
                            return (
                                <StyledTableSubHeaderCell
                                    key="po-details-placeholder-2"
                                    sx={{
                                        backgroundColor: "#f5f5f5",
                                        minWidth: "100px",
                                        width: "100px",
                                    }}
                                />
                            );
                        }
                        else if (col.type === 'week-based' && col.weekKeys && col.weekKeys.length > 0) {
                            return col.weekKeys.map((week, weekIndex) => {
                                const isStockAgeingMoreDetails =
                                    col.label === STOCK_AGEING_COLUMN &&
                                    week === STOCK_AGEING_MORE_DETAILS;

                                if (isStockAgeingMoreDetails) {
                                    return (
                                        <StyledTableSubHeaderCell
                                            key={`${col.label}-subtotal-${String(week)}`}
                                            sx={{
                                                backgroundColor: "#f5f5f5",
                                                borderLeft: weekIndex === 0
                                                    ? "1px solid #00000063 !important"
                                                    : "inherit",
                                            }}
                                        />
                                    );
                                }

                                const rawValue = getSubtotalRawValue(col, week);
                                const value = getRenderableCellValue(rawValue);

                                return (
                                    <StyledTableSubHeaderCell
                                        key={`${col.label}-subtotal-${String(week)}`}
                                        stockcoverage={col.category === 'maiPhysical' || col.category === 'maiTotal' ? value : undefined}
                                        type={col.category === 'maiPhysical' || col.category === 'maiTotal' ? 'RMPM Alert' : undefined}
                                        sx={{
                                            backgroundColor: '#f5f5f5',
                                            borderLeft: weekIndex === 0 ? '1px solid #00000063 !important' : 'inherit'
                                        }}
                                    >
                                        {value}
                                    </StyledTableSubHeaderCell>
                                );
                            });
                        } 
                        else {
                            return (
                                <StyledTableSubHeaderCell key={`${col.label}-subtotal`}>
                                </StyledTableSubHeaderCell>
                            );
                        }
                    })}
                </TableRow>
            </>
        );
    }, [columnsInDisplayOrder, fixedColumnLeftByLabel, expandedColumns, sortConfig, handleSort, getSubtotalRawValue]);

    const renderTableBody = useCallback(() => {
        if (sortedData.length === 0) {
            return (
                <StyledTableRow key="Data not available">
                    <StyledTableCell colSpan={totalRenderedColumns} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                        {loading ? "Loading..." : "Data not available..."}
                    </StyledTableCell>
                </StyledTableRow>
            );
        }

        const dataToRender = paginationObj || onPageChange
            ? sortedData
            : sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        return buildMaterialDisplayRows(dataToRender, groupByMaterialId).map(({ row: alert, materialId, showHeader }, rowIndex) => (
            <React.Fragment key={`${getStableRowKey(alert)}-${rowIndex}`}>
                {showHeader && (
                    <StyledTableRow data-testid="material-group-row">
                        <StyledTableCell
                            colSpan={totalRenderedColumns}
                            sx={{ textAlign: "left", fontWeight: 700, backgroundColor: "#F2F4F7" }}
                            style={{
                                textAlign: 'left',
                                color: 'black',
                                fontSize: '12px',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                            }}
                        >
                            {materialId}
                        </StyledTableCell>
                    </StyledTableRow>
                )}
            <StyledTableRow key={getStableRowKey(alert)}>
                {columnsInDisplayOrder.map((col) => {
                    if (col.isFixed) {
                        const isExpanded = expandedColumns[col.label];
                        const cellExpandKey = getCellExpandKey(getStableRowKey(alert), col.label);
                        const isCellExpanded = !!expandedCellValues[cellExpandKey];
                        const rawCellValue = trimQuotes(alert[col.label]);
                        const commaValues = supportsCommaExpand(col.label) ? getCommaSeparatedValues(rawCellValue) : [];
                        const previewValues = commaValues.slice(0, 5);
                        const remainingCount = Math.max(commaValues.length - 5, 0);
                        const commaSeparatedText = commaValues.join(", ");

                        let cellContent;
                        if (typeof alert[col.label] === 'object') {
                            cellContent = "-";
                        } else if (isExpanded && supportsCommaExpand(col.label) && commaValues.length > 0) {
                            if (isCellExpanded) {
                                if (commaValues.length <= 20) {
                                    cellContent = (
                                        <div className={styles.commaExpandContainer}>
                                            {commaValues.map((item) => (
                                                <span key={`${cellExpandKey}-expanded-${item}`} className={styles.commaLineItem}>
                                                    {item}
                                                </span>
                                            ))}
                                        </div>
                                    );
                                } else {
                                    cellContent = (
                                        <div className={styles.commaFullText}>
                                            {commaSeparatedText}
                                        </div>
                                    );
                                }
                            } else {
                                cellContent = (
                                    <div className={styles.commaExpandContainer}>
                                        {previewValues.map((item) => (
                                            <span key={`${cellExpandKey}-${item}`} className={styles.commaLineItem}>
                                                {item}
                                            </span>
                                        ))}
                                        {remainingCount > 0 && (
                                            <button
                                                type="button"
                                                className={styles.moreValuesBtn}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    setExpandedCellValues((prev) => ({
                                                        ...prev,
                                                        [cellExpandKey]: true,
                                                    }));
                                                }}
                                            >
                                                +{remainingCount}
                                            </button>
                                        )}
                                    </div>
                                );
                            }
                        } else {
                            cellContent = (
                                <CustomTooltip title={rawCellValue}>
                                    <span>{rawCellValue}</span>
                                </CustomTooltip>
                            );
                        }

                        return (
                            <StyledTableCell
                                className={!isExpanded ? styles.ellipsisText : ''}
                                key={`fixed-${col.label}`}
                                sx={{
                                    backgroundColor:'white',
                                    borderRight: '1px solid #e0e0e0',
                                    position: "sticky",
                                    left: fixedColumnLeftByLabel[col.label],
                                    minWidth: isExpanded ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                    maxWidth: isExpanded ? getExpandedWidthForColumn(col.label, '250px') : undefined,
                                    width: isExpanded ? getExpandedWidthForColumn(col.label, '250px') : (col.width || "100px"),
                                    whiteSpace: isExpanded ? 'normal' : 'nowrap',
                                    overflow: isExpanded ? 'visible' : 'hidden',
                                    textOverflow: isExpanded ? 'clip' : 'ellipsis',
                                    textAlign: isExpanded && isTruncatableColumn(col.label) ? 'left !important' : undefined,
                                }}
                            >
                                {cellContent}
                            </StyledTableCell>
                        );
                    }

                    if (col.type === 'po-details') {
                        return (
                            <StyledTableCell
                                key="po-details-cell"
                                className={styles.insightBtn}
                                onClick={() => handlePODetailsClick(alert)}
                            >
                                {col.label === 'PO Details' ? 'More Details' : col.label}
                            </StyledTableCell>
                        );
                    } else if (col.type === 'week-based' && col.weekKeys && col.weekKeys.length > 0) {
                        return col.weekKeys.map((week, weekIndex) => {
                            const rawValue = col.category === 'stockVsNorm'
                                ? (alert[col.label]?.[week] ?? alert[week])
                                : alert[col.label]?.[week];
                            const value = getRenderableCellValue(rawValue);
                            const isFirstColumn = weekIndex === 0;
                            const isStockAgeingMoreDetails =
                                col.label === STOCK_AGEING_COLUMN &&
                                week === STOCK_AGEING_MORE_DETAILS;

                            if (isStockAgeingMoreDetails) {
                                return (
                                    <StyledTableCell
                                        key={`${col.label}-${String(week)}`}
                                        className={styles.insightBtn}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleStockAgeingDetailsClick(alert);
                                        }}
                                        sx={{
                                            borderLeft: isFirstColumn
                                                ? "1px solid #00000063 !important"
                                                : "inherit",
                                        }}
                                    >
                                        More Details
                                    </StyledTableCell>
                                );
                            }

                            // Special handling for MAI % (Total) with tooltip
                            if (col.label === 'MAI % (Total)') {
                                const demand = alert["Demand Tooltip"]?.[week];
                                const supply = alert["Supply (Physical) Tooltip"]?.[week];
                                const openingStock = alert['Opening Stock']?.[week];
                                const openPos = alert['Open POs']?.[week];
                                const materialId = alert["Material Id"] || '-';
                                const siteCode = alert["Site code"] || '-';
                                const thresholdWeekNumber = alert['MAI % Threshold Week'];
                                const currentWeekNumber = parseInt(String(week).replace(/\D/g, ''), 10);
                                const isAfterThresholdWeek =
                                    typeof thresholdWeekNumber === "number" && Number.isFinite(thresholdWeekNumber) &&
                                    Number.isFinite(currentWeekNumber) &&
                                    thresholdWeekNumber >= current_week && currentWeekNumber >= thresholdWeekNumber;

                                const tooltipContent = (
                                    <div className={styles.customTooltip}>
                                        <p><span className={styles.label}>Site Code :</span> {siteCode}</p>
                                        <p><span className={styles.label}>Material ID :</span> {materialId}</p>
                                        <p><span className={styles.label}>Demand :</span> {demand}</p>
                                        <p><span className={styles.label}>Stock :</span> {supply}</p>
                                        <p><span className={styles.label}>Opening Stock :</span> {openingStock}</p>
                                        <p><span className={styles.label}>Open PO QTY :</span> {openPos}</p>
                                    </div>
                                );

                                const maiTotalBgColor = getMaiTotalHeatMapColor(isAfterThresholdWeek, value);

                                return (
                                    <StyledTableCell
                                        key={`${col.label}-${String(week)}`}
                                        sx={{
                                            backgroundColor: maiTotalBgColor,
                                            borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit',
                                            cursor: "pointer"
                                        }}
                                        stockcoverage={value}
                                        type={STOCK_COLOR_ALERT}
                                    >
                                        <Tooltip title={tooltipContent}
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        backgroundColor: "transparent",
                                                        boxShadow: "none",
                                                        padding: 0,
                                                    },
                                                },
                                            }}>
                                            <span style={{ display: 'block', width: '100%', height: '100%' }}>{value}</span>
                                        </Tooltip>
                                    </StyledTableCell>
                                );
                            }

                            // Special handling for MAI % (Physical)
                            if (col.label === 'MAI % (Physical)') {
                                return (
                                    <StyledTableCell
                                        key={`${col.label}-${String(week)}`}
                                        type={STOCK_COLOR_ALERT}
                                        sx={{
                                            backgroundColor: getDefaultHeatMapColor(value),
                                            borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit'
                                        }}
                                        stockcoverage={value}
                                    >
                                        {value}
                                    </StyledTableCell>
                                );
                            }

                            // Special handling for Inter-unit transfer view
                            if (col.label === 'Inter-unit transfer view') {
                                return (
                                    <StyledTableCell
                                        key={`${col.label}-${String(week)}`}
                                        sx={{
                                            borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit'
                                        }}
                                    >
                                        {value === undefined || value === null || value === '' ? '-' :
                                            <CustomTooltip title={trimQuotes(value)}>
                                                <span>{trimQuotes(value)}</span>
                                            </CustomTooltip>
                                        }
                                    </StyledTableCell>
                                );
                            }

                            if (col.category === 'stockVsNorm') {
                                if (week === 'Stock Cover (Days)') {
                                    return (
                                        <StyledTableCell
                                            key={`${col.label}-${String(week)}`}
                                            sx={{
                                                borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit'
                                            }}
                                        >
                                            <CustomTooltip title={get_tooltip(value)}>
                                                <span>{value}</span>
                                            </CustomTooltip>
                                        </StyledTableCell>
                                    );
                                }

                                return (
                                    <StyledTableCell
                                        key={`${col.label}-${String(week)}`}
                                        type={shouldApplyCellColor(week) ? 'RMPM Alert' : undefined}
                                        sx={{
                                            backgroundColor: shouldApplyCellColor(week)
                                                ? getDefaultHeatMapColor(value)
                                                : "inherit",
                                            borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit'
                                        }}
                                        stockcoverage={shouldApplyCellColor(week) ? value : undefined}
                                    >
                                        {value}
                                    </StyledTableCell>
                                );
                            }

                            if(col.category==='physicalStock' && col.label==='Physical Stock (Qty)'){
                                    if(week==='Supplier Stock'){
                                        return (
                                        <StyledTableCell
                                            key={`${col.label}-${String(week)}`}
                                            className={value!=='-'?styles.insightBtn:styles.insightBtnDisabled}
                                            onClick={() => handleSupplierDetailsClick(alert)}
                            >
                                {value}
                            </StyledTableCell>
                        ); }
                    }

                            // Default rendering for other week-based columns
                            return (
                                <StyledTableCell
                                    key={`${col.label}-${String(week)}`}
                                    sx={{
                                        borderLeft: isFirstColumn ? '1px solid #00000063 !important' : 'inherit'
                                    }}
                                    stockcoverage={shouldApplyCellColor(col.label) ? value : undefined}
                                >
                                    {value}
                                </StyledTableCell>
                            );
                        });
                    } else {
                        // Simple column rendering
                        const value = alert?.[col.label];
                        
                        // Check if value is an object (shouldn't be rendered directly)
                        if (typeof value === 'object' && value !== null) {
                            return (
                                <StyledTableCell key={`simple-cell-${col.label}`}>
                                    -
                                </StyledTableCell>
                            );
                        }
                        
                        // Special handling for Stock to Norm %
                        if (col.label === 'Stock to Norm %') {
                            return (
                                <StyledTableCell
                                    key={`simple-cell-${col.label}`}
                                    type={STOCK_COLOR_ALERT}
                                    sx={{
                                        backgroundColor: getDefaultHeatMapColor(value),
                                        borderLeft: 'inherit'
                                    }}
                                    stockcoverage={value}
                                >
                                    {value}
                                </StyledTableCell>
                            );
                        }
                        
                        // Special handling for Stock Cover (Days)
                        if (col.label === 'Stock Cover (Days)') {
                            return (
                                <StyledTableCell key={`simple-cell-${col.label}`}>
                                    <CustomTooltip title={get_tooltip(value)}>
                                        <span>{value}</span>
                                    </CustomTooltip>
                                </StyledTableCell>
                            );
                        }
                        
                        // Default simple column
                        return (
                            <StyledTableCell key={`simple-cell-${col.label}`}>
                                {value === 0 ? 0 : renderCommaSeparatedValueWithEllipsis(value)}
                            </StyledTableCell>
                        );
                    }
                })}
            </StyledTableRow>
            </React.Fragment>
        ));
    }, [sortedData, loading, page, rowsPerPage, paginationObj, onPageChange, columnsInDisplayOrder, totalRenderedColumns, fixedColumnLeftByLabel, expandedColumns, expandedCellValues, current_week, shouldApplyCellColor, groupByMaterialId]);

    
        const columnDescriptionByNormalizedLabel = useMemo(() => {
            const map = new Map();
            Object.entries(columnDesc["MAI View"] || {}).forEach(([label, desc]) => {
                map.set(label, desc);
            });
            return map;
        }, [columnDesc]);
    
        
    
     const getColumnDescription = useCallback((label) => (
            columnDescriptionByNormalizedLabel.get(label) || ""
        ), [columnDescriptionByNormalizedLabel]);
    
        const handleOpenColumnInfo = (event, label) => {
            event.stopPropagation();
            const description = getColumnDescription(label);
            if (!description) {
                return;
            }
            setColumnInfoText(description);
            setColumnInfoAnchor(event.currentTarget);
        };
    
        const handleCloseColumnInfo = () => {
            setColumnInfoAnchor(null);
            setColumnInfoText("");
        };
    
    return (
        <>
            <Box sx={{ position: 'relative' }}>
                {isProcessing && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}

                <TableContainer 
                    className={`${styles.table} ${styles.pageScrollTable} ${styles.maiTableHeight}`}
                    sx={{
                        '&::-webkit-scrollbar': {
                            width: '7px',
                            height: '7px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '7px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            borderRadius: '7px',
                        }
                    }}
                >
                    <Table size="small" stickyHeader>
                        <TableHead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "white" }}>
                            {renderMainHeader()}
                            {renderWeekHeaders()}
                        </TableHead>
                        <TableBody>
                            {renderTableBody()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Box className={styles.bottomBar} >
                <div className={styles.contractLegends}>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#D3D3D3" }}></span>
                        <span>{'Beyond Lead Time'}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#feb0af" }}></span>
                        <span>{'< 75%'}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#FFF9C4" }}></span>
                        <span>{'75% - 90%'}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#A3E1D2" }}></span>
                        <span>{'90% - 125%'}</span>
                    </div>
                    <div className={styles.contractLegendsDiv}>
                        <span className={styles.legends} style={{ backgroundColor: "#2196f33d" }}></span>
                        <span>{'> 125%'}</span>
                    </div>
                </div>
                <AlertPagination
                    paginationObj={paginationObj}
                    onPageChange={onPageChange}
                    localPage={page}
                    rowsPerPage={rowsPerPage}
                    totalRecords={sortedData.length}
                    onChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
            </Box >
            {showSupplierDetailsPopup &&
                <SupplierPopup
                    type={showSupplierDetailsPopup}
                    tableData={supplierDetailsData}
                    handleAlertPopup={handleSupplierDetailsPopup}
                />
            }
            {showStockAgeingDetailsPopup &&
                <SupplierPopup
                    type={showStockAgeingDetailsPopup}
                    tableData={stockAgeingDetailsData["rowData"]}
                    handleAlertPopup={handleStockAgeingDetailsPopup}
                />
            }
            {showPODetailsPopup &&
                <PODetailsMAI
                    rowData={poDetailsData.rowData}
                    type={showPODetailsPopup}
                    tableData={poDetailsData.tableData}
                    handleAlertPopup={handlePODetailsPopup}
                />}
            <Popover
                open={Boolean(columnInfoAnchor)}
                anchorEl={columnInfoAnchor}
                onClose={handleCloseColumnInfo}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <Typography sx={{ p: 1.5, fontSize: '12px', maxWidth: '420px', whiteSpace: 'normal' }}>
                    {columnInfoText}
                </Typography>
            </Popover>
        </>
    );
};



MAITable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columnList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
            width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    paginationObj: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        total_pages: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    loading: PropTypes.bool,
    groupByMaterialId: PropTypes.bool,
    perWeekMai: PropTypes.object,
    perWeekMaiPhysical: PropTypes.object,
    totalStockToNorm: PropTypes.number,
    subtotal: PropTypes.object,
    onSortChange: PropTypes.func,
    sortConfig: PropTypes.shape({
        key: PropTypes.string,
        direction: PropTypes.string,
        week: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
    }),
};

MAITable.defaultProps = {
    data: [],
    columnList: [],
    paginationObj: null,
    onPageChange: null,
    loading: false,
    perWeekMai: {},
    perWeekMaiPhysical: {},
    totalStockToNorm: 0,
    subtotal: {},
    onSortChange: null,
    sortConfig: { key: "", direction: "asc", week: null },
};

export default React.memo(MAITable);